// MAP FOES (owner 2026-11-02: 'more enemy variety') — art for the map-only foes (src/core/mapFoeDefs.ts). Same kit as the
// shared families (kit.ts): one merged, vertex-coloured body + glow geometry per kind, instanced, GPU gait / wind-up /
// wing flap; feet at y = 0, facing +Z. Each is drawn in its own map's palette (no map skin is applied on top) and is
// built only on its map (renderer.ts). Original procedural designs. Map foes are never elites (build ignores `elite`).
import * as THREE from 'three/webgpu';
import { ArtBuilder, ArtGeo, at, EnemyArt, limb, PAL, plate } from './kit';
import { blob, drum, spike } from './parts_heavy';
import { facetWing, mirrorX, shard } from './parts_swarm';
import type { MapFoeKind } from '../../core/mapFoeDefs';

type V3 = [number, number, number];
const legP = (s: number) => (s > 0 ? 0 : Math.PI);

// ---------------------------------------------------------------- Canyon Meadow · Loam Delver (burrower)
// A low mole-beetle: ochre shell in three overlapping bands, a pale ivory drill snout of stacked cones, two broad
// shovel claws that dig (they rear up on the wind-up), stubby legs, teal eyes and a teal seam along the spine.
function delver(): ArtGeo {
  const b = new ArtBuilder();
  const SHELL = 0x8a6a3a, SHELL_L = 0xb08850, BELLY = 0xd2b07a;
  b.add(blob(1, 0, 0.42, -0.05, [0.62, 0.36, 0.78]), { color: BELLY });
  for (const [z, s, c] of [[0.22, 0.6, SHELL_L], [-0.08, 0.66, SHELL], [-0.38, 0.56, SHELL_L]] as [number, number, number][]) b.add(blob(1, 0, 0.55, z, [s, 0.34, 0.32]), { color: c });
  b.add(limb([0, 0.82, 0.3], [0, 0.86, -0.55], 0.035, 0.02, 5), { color: PAL.teal, glow: true }); // spinal seam
  // drill snout (raises on the wind-up)
  const head = { pivot: [0, 0.45, 0.5] as V3, raise: -0.45 };
  b.add(blob(0.24, 0, 0.45, 0.55, [1, 0.85, 1]), { color: SHELL, ...head });
  for (const [z, r, h] of [[0.72, 0.2, 0.22], [0.86, 0.15, 0.2], [0.99, 0.09, 0.18]] as [number, number, number][]) b.add(spike(r, h, 0, 0.44, z, Math.PI / 2), { color: z > 0.9 ? PAL.ivory : PAL.ivoryShade, ...head });
  for (const s of [-1, 1]) b.add(blob(0.05, s * 0.13, 0.55, 0.68), { color: PAL.teal, glow: true, ...head });
  for (const s of [-1, 1]) {
    // shovel claws: swing while walking, rear up to dig
    const sh: V3 = [s * 0.42, 0.42, 0.38], arm = { pivot: sh, swing: 0.5, sphase: legP(s), raise: -0.9 };
    b.add(limb(sh, [s * 0.52, 0.16, 0.62], 0.08, 0.06, 6), { color: SHELL, ...arm });
    b.add(at(plate(0.26, 0.05, 0.3, 0.02), s * 0.55, 0.08, 0.72, 0.3, s * 0.2, 0), { color: PAL.ivory, ...arm });
    for (const k of [-1, 0, 1]) b.add(spike(0.03, 0.14, s * 0.55 + k * 0.08, 0.04, 0.9, Math.PI / 2 + 0.2), { color: PAL.iron, ...arm });
    // stubby hind legs
    const hp: V3 = [s * 0.34, 0.3, -0.3];
    b.add(limb(hp, [s * 0.44, 0.04, -0.34], 0.07, 0.05, 6), { color: SHELL, pivot: hp, swing: 0.55, sphase: legP(-s) });
  }
  return b.build();
}

// ---------------------------------------------------------------- Shattered Metropolis · Hush Beacon (signal jammer)
// A hovering hex-drum relay: dark street-iron body with a sodium-lamp band, a tilted dish on top, a cyan signal core
// that pulses, three dangling cable tendrils and two small stabiliser fins.
function jammer(): ArtGeo {
  const b = new ArtBuilder();
  const BODY = 0x4a4f5c, BODY_L = 0x6c717c, LAMP = 0xffa940, SIG = 0x6ff2ff;
  b.add(drum(0.3, 0.26, 0.36, 0, 0.42, 0, 0, 0, 0, 6), { color: BODY });
  b.add(drum(0.33, 0.33, 0.07, 0, 0.5, 0, 0, 0, 0, 6), { color: LAMP, glow: true });
  b.add(drum(0.2, 0.3, 0.1, 0, 0.66, 0, 0, 0, 0, 6), { color: BODY_L });
  // dish (tilts back on the wind-up)
  const dish = { pivot: [0, 0.72, 0] as V3, raise: 0.4 };
  b.add(limb([0, 0.7, 0], [0, 0.92, 0.05], 0.03, 0.025, 5), { color: PAL.iron, ...dish });
  b.add(at(new THREE.SphereGeometry(0.22, 10, 4, 0, Math.PI * 2, 0, 1.0), 0, 0.98, 0.08, -0.55, 0, 0), { color: BODY_L, ...dish });
  b.add(blob(0.05, 0, 1.02, 0.14), { color: SIG, glow: true, ...dish });
  b.add(blob(0.13, 0, 0.36, 0.25, [1, 1, 0.6]), { color: SIG, glow: true }); // signal core (front)
  for (let k = 0; k < 3; k++) { // cable tendrils
    const a = (k / 3) * Math.PI * 2 + 0.5, x = Math.cos(a) * 0.17, z = Math.sin(a) * 0.17;
    b.add(limb([x, 0.26, z], [x * 1.3, 0.02, z * 1.3 - 0.05], 0.022, 0.012, 4), { color: PAL.iron, pivot: [x, 0.26, z], swing: 0.25, sphase: k * 2 });
    b.add(blob(0.03, x * 1.3, 0.02, z * 1.3 - 0.05), { color: SIG, glow: true, pivot: [x, 0.26, z], swing: 0.25, sphase: k * 2 });
  }
  for (const s of [-1, 1]) b.add(at(plate(0.26, 0.03, 0.18, 0.01), s * 0.38, 0.46, -0.04, 0, 0, s * 0.25), { color: BODY_L, pivot: [s * 0.28, 0.46, 0], flap: s * 0.35 });
  return b.build();
}

// ---------------------------------------------------------------- Rust Canyon · Rust Roller (tumble brute)
// An armadillo-like brute under five overlapping bands of rusted plate with copper rivets and amber seams; a blunt
// ram-skull head with short horns, thick short legs. It tucks into a ball to roll (render spins the whole body).
function roller(): ArtGeo {
  const b = new ArtBuilder();
  const RUST = 0xa0482a, RUST_D = 0x6e3020, OCHRE = 0xc8823a, AMBER = 0xffa030;
  b.add(blob(1, 0, 0.78, 0, [0.82, 0.62, 0.95]), { color: RUST_D });
  for (let k = 0; k < 5; k++) {
    const z = -0.62 + k * 0.31, r = 0.86 - Math.abs(k - 2) * 0.09;
    b.add(at(new THREE.TorusGeometry(r, 0.11, 5, 14, Math.PI), 0, 0.72, z, 0, 0, 0, [1, 0.95, 1]), { color: k % 2 ? RUST : OCHRE }); // a band over the back
    b.add(blob(0.045, 0, 0.72 + r + 0.08, z), { color: PAL.copper });
  }
  b.add(limb([0.0, 1.5, -0.55], [0, 1.52, 0.6], 0.04, 0.04, 5), { color: AMBER, glow: true }); // spine seam
  // head (tucks down on the curl)
  const head = { pivot: [0, 0.75, 0.75] as V3, raise: 0.9 };
  b.add(blob(0.3, 0, 0.62, 0.98, [1, 0.85, 1.15]), { color: RUST_D, ...head });
  b.add(at(plate(0.42, 0.22, 0.2, 0.05), 0, 0.68, 1.22), { color: OCHRE, ...head });
  for (const s of [-1, 1]) {
    b.add(spike(0.07, 0.34, s * 0.24, 0.86, 1.0, 0.4, 0, -s * 0.7), { color: PAL.ivoryShade, ...head });
    b.add(blob(0.045, s * 0.13, 0.7, 1.29), { color: AMBER, glow: true, ...head });
  }
  for (const s of [-1, 1]) for (const z of [0.45, -0.45]) {
    const hp: V3 = [s * 0.55, 0.5, z];
    b.add(limb(hp, [s * 0.62, 0.05, z + 0.05], 0.13, 0.11, 6), { color: RUST_D, pivot: hp, swing: 0.5, sphase: legP(z > 0 ? s : -s), raise: -0.8 });
  }
  return b.build();
}

// ---------------------------------------------------------------- Verdant Alien · Bloat Pod (spore pod)
// A rooted bulb on a short stalk inside a cup of five fleshy petals: teal-green skin with purple veins and glowing
// lime spore blisters; the bulb swells (raise) before it bursts.
function sporepod(): ArtGeo {
  const b = new ArtBuilder();
  const SKIN = 0x2f8f6a, SKIN_L = 0x55b88a, VEIN = 0x7a3fa0, SPORE = 0xc8ff4a;
  for (let k = 0; k < 5; k++) {
    const a = (k / 5) * Math.PI * 2;
    b.add(at(new THREE.SphereGeometry(1, 8, 5), Math.cos(a) * 0.42, 0.14, Math.sin(a) * 0.42, 0, -a, 0.9, [0.38, 0.08, 0.2]), { color: k % 2 ? SKIN : VEIN });
  }
  b.add(drum(0.16, 0.24, 0.42, 0, 0.22, 0), { color: SKIN });
  const bulb = { pivot: [0, 0.42, 0] as V3, raise: 0.25 };
  b.add(blob(1, 0, 0.82, 0, [0.58, 0.55, 0.58]), { color: SKIN_L, ...bulb });
  for (let k = 0; k < 6; k++) { const a = (k / 6) * Math.PI * 2; b.add(limb([Math.cos(a) * 0.2, 0.42, Math.sin(a) * 0.2], [Math.cos(a) * 0.5, 0.92, Math.sin(a) * 0.5], 0.03, 0.02, 4), { color: VEIN, ...bulb }); }
  for (let k = 0; k < 7; k++) {
    const a = k * 2.4, y = 0.62 + (k % 3) * 0.18, rr = Math.sqrt(Math.max(0, 0.3 - (y - 0.82) ** 2 / 0.3)) * 0.98 + 0.04;
    b.add(blob(0.08 + (k % 2) * 0.03, Math.cos(a) * rr, y, Math.sin(a) * rr), { color: SPORE, glow: true, ...bulb });
  }
  b.add(spike(0.1, 0.22, 0, 1.38, 0), { color: VEIN, ...bulb });
  return b.build();
}

// ---------------------------------------------------------------- Verdant Alien · Lash Creeper (vine lasher)
// A walking stalk on three root legs: twisted teal bark trunk, a crown of broad leaves around one magenta eye, a short
// left claw-branch and a long coiled whip vine on the right that draws back on the wind-up.
function lasher(): ArtGeo {
  const b = new ArtBuilder();
  const BARK = 0x2a5a4a, BARK_L = 0x3f7a5a, LEAF = 0x4aa04a, LEAF_D = 0x2f7a3a, EYE = 0xff3ca8;
  for (let k = 0; k < 3; k++) { // root legs
    const a = (k / 3) * Math.PI * 2 + Math.PI / 2, hp: V3 = [Math.cos(a) * 0.12, 0.55, Math.sin(a) * 0.12];
    b.add(limb(hp, [Math.cos(a) * 0.42, 0.02, Math.sin(a) * 0.42], 0.08, 0.04, 5), { color: BARK, pivot: hp, swing: 0.45, sphase: k * 2.1 });
  }
  b.add(limb([0, 0.5, 0], [0.05, 1.35, 0.02], 0.17, 0.12, 7), { color: BARK_L });
  b.add(limb([0.04, 0.6, 0.05], [-0.06, 1.3, -0.04], 0.06, 0.04, 5), { color: BARK });
  // crown + eye
  for (let k = 0; k < 6; k++) { const a = (k / 6) * Math.PI * 2; b.add(at(new THREE.SphereGeometry(1, 6, 4), Math.cos(a) * 0.24, 1.48, Math.sin(a) * 0.24, 0, -a, -0.6, [0.32, 0.05, 0.14]), { color: k % 2 ? LEAF : LEAF_D }); }
  b.add(blob(0.16, 0, 1.46, 0.06), { color: BARK_L });
  b.add(blob(0.08, 0, 1.47, 0.2), { color: EYE, glow: true });
  // left claw-branch
  b.add(limb([-0.14, 1.15, 0.02], [-0.42, 0.85, 0.22], 0.05, 0.03, 5), { color: BARK, pivot: [-0.14, 1.15, 0], swing: 0.4, sphase: 0 });
  // right whip vine: thick base, coiled segments, a thorned tip (draws back on the wind-up)
  const whip = { pivot: [0.16, 1.15, 0] as V3, swing: 0.3, sphase: Math.PI, raise: -1.2 };
  const pts: V3[] = [[0.16, 1.15, 0], [0.42, 1.05, 0.25], [0.55, 0.8, 0.55], [0.5, 0.55, 0.85], [0.35, 0.42, 1.1], [0.2, 0.45, 1.35]];
  for (let i = 0; i < pts.length - 1; i++) b.add(limb(pts[i], pts[i + 1], 0.07 - i * 0.011, 0.06 - i * 0.011, 5), { color: i % 2 ? BARK_L : BARK, ...whip });
  for (let i = 1; i < pts.length; i++) b.add(spike(0.025, 0.1, pts[i][0] + 0.05, pts[i][1] + 0.05, pts[i][2], 0, 0, -0.8), { color: EYE, glow: i === pts.length - 1, ...whip });
  return b.build();
}

// ---------------------------------------------------------------- Glacier Ruins · Rime Wraith
// A hovering hooded shroud of ice: a tattered cone of pale ice plates over a deep-blue hollow, two trailing sleeves, a
// cold cyan face-light and a ring of frost shards circling the hem.
function wraith(): ArtGeo {
  const b = new ArtBuilder();
  const ICE = 0xcfeaff, ICE_D = 0x7aa8d8, DEEP = 0x2a4a8a, GLOW = 0x9ff4ff;
  b.add(at(new THREE.ConeGeometry(0.42, 1.0, 9, 1, true), 0, 0.45, 0, Math.PI), { color: ICE_D });
  for (let k = 0; k < 7; k++) { const a = (k / 7) * Math.PI * 2; b.add(shard([Math.cos(a) * 0.32, 0.2, Math.sin(a) * 0.32], [Math.cos(a) * 0.46, -0.18, Math.sin(a) * 0.46], 0.07, 4), { color: k % 2 ? ICE : ICE_D }); }
  // hood (dips on the wind-up)
  const hood = { pivot: [0, 0.75, 0] as V3, raise: 0.4 };
  b.add(blob(1, 0, 0.98, -0.03, [0.27, 0.32, 0.27]), { color: ICE, ...hood });
  b.add(blob(1, 0, 0.95, 0.07, [0.17, 0.2, 0.12]), { color: DEEP, ...hood });
  for (const s of [-1, 1]) b.add(blob(0.04, s * 0.06, 0.97, 0.17), { color: GLOW, glow: true, ...hood });
  b.add(spike(0.1, 0.3, 0, 1.3, -0.1, -0.5), { color: ICE, ...hood });
  for (const s of [-1, 1]) { // sleeves: trail and flutter
    const sh: V3 = [s * 0.22, 0.78, 0];
    b.add(limb(sh, [s * 0.5, 0.42, 0.22], 0.08, 0.04, 5), { color: ICE, pivot: sh, swing: 0.35, sphase: legP(s), flap: s * 0.3 });
    b.add(shard([s * 0.5, 0.42, 0.22], [s * 0.58, 0.18, 0.32], 0.05, 4), { color: GLOW, glow: true, pivot: sh, swing: 0.35, sphase: legP(s), flap: s * 0.3 });
  }
  return b.build();
}

// ---------------------------------------------------------------- Ember Wastes · Slag Hopper
// A squat basalt toad with big folded hind legs (it crouches deep on the wind-up), short forelegs, a wide mouth slit
// and molten cracks glowing orange across the back.
function hopper(): ArtGeo {
  const b = new ArtBuilder();
  const ROCK = PAL.basalt, ROCK_L = PAL.basaltLight, LAVA = 0xff7a2a, HOT = 0xffc040;
  b.add(blob(1, 0, 0.5, -0.05, [0.5, 0.36, 0.58]), { color: ROCK });
  b.add(blob(1, 0, 0.6, 0.32, [0.36, 0.26, 0.3]), { color: ROCK_L });
  b.add(at(plate(0.42, 0.04, 0.06, 0.015), 0, 0.5, 0.58), { color: LAVA, glow: true }); // mouth
  for (const s of [-1, 1]) b.add(blob(0.07, s * 0.2, 0.78, 0.38), { color: HOT, glow: true });
  for (const [a, c] of [[[0.08, 0.88, 0.12], [-0.1, 0.87, -0.12]], [[-0.1, 0.87, -0.12], [0.06, 0.82, -0.38]], [[-0.22, 0.83, 0.08], [-0.3, 0.76, -0.2]], [[0.24, 0.8, -0.05], [0.4, 0.66, -0.3]], [[-0.36, 0.7, -0.1], [-0.44, 0.56, -0.32]]] as [V3, V3][]) b.add(limb(a, c, 0.045, 0.035, 4), { color: LAVA, glow: true }); // molten cracks over the back
  b.add(blob(1, 0, 0.3, 0.02, [0.36, 0.1, 0.42]), { color: 0xd04a18 }); // ember belly
  for (const s of [-1, 1]) {
    // hind legs: big thigh + shin, crouch on the wind-up
    const hp: V3 = [s * 0.36, 0.45, -0.3], leg = { pivot: hp, swing: 0.35, sphase: 0, raise: 0.6 };
    b.add(blob(1, s * 0.48, 0.42, -0.32, [0.17, 0.24, 0.32]), { color: ROCK_L, ...leg });
    b.add(limb([s * 0.52, 0.28, -0.12], [s * 0.56, 0.04, -0.4], 0.08, 0.06, 6), { color: ROCK, ...leg });
    b.add(at(plate(0.18, 0.04, 0.3, 0.015), s * 0.56, 0.03, -0.32), { color: ROCK, ...leg });
    const fp: V3 = [s * 0.22, 0.38, 0.32];
    b.add(limb(fp, [s * 0.3, 0.03, 0.46], 0.05, 0.04, 5), { color: ROCK, pivot: fp, swing: 0.3, sphase: Math.PI });
  }
  return b.build();
}

// ---------------------------------------------------------------- Ember Wastes · Cinder Mite
// A thumb-sized ember beetle: a cracked black shell over a white-hot core, six twitching legs and two spark antennae.
function mite(): ArtGeo {
  const b = new ArtBuilder();
  for (const sx of [-1, 1]) b.add(blob(1, sx * 0.085, 0.24, -0.01, [0.13, 0.13, 0.27], 10, 7, 0, 0, sx * 0.25), { color: PAL.basalt }); // split wing-cases
  b.add(blob(1, 0, 0.25, 0, [0.08, 0.12, 0.25]), { color: 0xffc050, glow: true }); // the white-hot core between them
  b.add(blob(1, 0, 0.16, 0.02, [0.18, 0.06, 0.22]), { color: 0xff6a20, glow: true });
  b.add(blob(0.08, 0, 0.22, 0.28), { color: PAL.basaltLight });
  for (const s of [-1, 1]) {
    b.add(limb([s * 0.04, 0.26, 0.32], [s * 0.12, 0.38, 0.46], 0.012, 0.008, 3), { color: 0xffd060, glow: true });
    for (const z of [0.12, 0, -0.12]) { const hp: V3 = [s * 0.15, 0.2, z]; b.add(limb(hp, [s * 0.28, 0.02, z + 0.04], 0.02, 0.012, 3), { color: PAL.iron, pivot: hp, swing: 0.6, sphase: legP(z === 0 ? -s : s) }); }
  }
  return b.build();
}

// ---------------------------------------------------------------- Floating Isles · Gust Kite
// A living kite: a diamond sail of cream cloth on a gold frame with a sky-teal eye at its heart, two flapping side
// fins and a long ribbon tail. The sail tilts back as it draws breath for a gust.
function kite(): ArtGeo {
  const b = new ArtBuilder();
  const SAIL = 0xf1ead8, SAIL_D = 0xd6c8a8, TEAL = 0x5ec8d8, GOLD = PAL.gold;
  const tilt = { pivot: [0, 0.6, 0] as V3, raise: 0.6 };
  const top: V3 = [0, 1.1, 0], bot: V3 = [0, 0.1, 0], l: V3 = [-0.45, 0.68, 0], r: V3 = [0.45, 0.68, 0];
  const sail = new THREE.BufferGeometry();
  const P = [top, l, bot, top, bot, r].flat(), Q = [top, bot, l, top, r, bot].flat();
  sail.setAttribute('position', new THREE.Float32BufferAttribute([...P.map((v, i) => (i % 3 === 2 ? v + 0.02 : v)), ...Q.map((v, i) => (i % 3 === 2 ? v - 0.02 : v))], 3));
  b.add(sail, { color: SAIL, ...tilt });
  for (const [a, c] of [[top, l], [l, bot], [bot, r], [r, top], [top, bot], [l, r]] as [V3, V3][]) b.add(limb(a, c, 0.025, 0.025, 4), { color: GOLD, ...tilt });
  b.add(blob(1, 0, 0.68, 0.05, [0.13, 0.13, 0.06]), { color: SAIL_D, ...tilt });
  b.add(blob(0.07, 0, 0.68, 0.09), { color: TEAL, glow: true, ...tilt });
  for (const s of [-1, 1]) {
    const root: V3 = [s * 0.4, 0.68, 0];
    const fin = facetWing(root, [[s * 0.42, 0.78, 0.05], [s * 0.72, 0.74, -0.05], [s * 0.8, 0.6, -0.18], [s * 0.5, 0.58, -0.12]], 0.02);
    b.add(s > 0 ? fin : mirrorX(facetWing([0.4, 0.68, 0], [[0.42, 0.78, 0.05], [0.72, 0.74, -0.05], [0.8, 0.6, -0.18], [0.5, 0.58, -0.12]], 0.02)), { color: TEAL, pivot: root, flap: s * 0.8 });
  }
  for (let i = 0; i < 4; i++) { // ribbon tail
    const y0 = 0.1 - i * 0.06, z0 = -0.05 - i * 0.18;
    b.add(at(plate(0.12, 0.02, 0.14, 0.005), 0, y0, z0, 0.3, 0, 0), { color: i % 2 ? TEAL : GOLD, pivot: [0, 0.1, 0], swing: 0.3, sphase: i * 0.7 });
  }
  return b.build();
}

// ---------------------------------------------------------------- Floating Isles · Sky Ray
// A broad cloud manta: white-backed wings with gold edge markings, a teal belly, two small horn-fins at the mouth and
// a long whip tail. Flaps slowly; the wings sweep back for the dive.
function skyray(): ArtGeo {
  const b = new ArtBuilder();
  const BACK = 0xf4f6fa, BELLY = 0x5ec8d8, GOLD = PAL.gold;
  b.add(blob(1, 0, 0.42, 0, [0.34, 0.14, 0.62]), { color: BACK });
  b.add(blob(1, 0, 0.36, 0.02, [0.3, 0.1, 0.56]), { color: BELLY });
  for (const s of [-1, 1]) {
    const root: V3 = [0.2, 0.42, 0.1];
    const rim: V3[] = [[0.25, 0.44, 0.5], [0.7, 0.42, 0.3], [1.15, 0.36, -0.05], [0.9, 0.38, -0.25], [0.5, 0.4, -0.38], [0.22, 0.42, -0.4]];
    const w = facetWing(root, rim, 0.03, 0.05), wing = { pivot: [s * 0.2, 0.42, 0] as V3, flap: s * 0.6, raise: -0.3 };
    b.add(s > 0 ? w : mirrorX(w), { color: BACK, ...wing });
    b.add(s > 0 ? limb([0.7, 0.45, 0.3], [1.15, 0.39, -0.05], 0.025, 0.015, 4) : mirrorX(limb([0.7, 0.45, 0.3], [1.15, 0.39, -0.05], 0.025, 0.015, 4)), { color: GOLD, ...wing });
    b.add(spike(0.05, 0.22, s * 0.16, 0.4, 0.66, Math.PI / 2 - 0.2, 0, s * 0.3), { color: BELLY });
    b.add(blob(0.045, s * 0.12, 0.48, 0.5), { color: 0xfff2a0, glow: true });
  }
  for (let i = 0; i < 3; i++) b.add(limb([0, 0.42, -0.55 - i * 0.3], [0, 0.42, -0.85 - i * 0.3], 0.04 - i * 0.012, 0.03 - i * 0.01, 4), { color: i === 2 ? GOLD : BACK, pivot: [0, 0.42, -0.55], swing: 0.35, sphase: i * 0.8 });
  return b.build();
}

// ---------------------------------------------------------------- Crystal Desert · Dune Lurker
// A flat antlion-crab the colour of the dunes: a wide segmented shell sprouting violet crystal spines, six splayed legs
// and two huge serrated mandibles that spread on the wind-up; small cyan eyes.
function lurker(): ArtGeo {
  const b = new ArtBuilder();
  const SAND = 0xb8945e, SAND_D = 0x7e6038, CRYS = 0xb58aff, EYE = 0x7ff0ff;
  b.add(blob(1, 0, 0.36, -0.1, [0.62, 0.22, 0.72]), { color: SAND });
  b.add(blob(1, 0, 0.42, 0.36, [0.38, 0.18, 0.3]), { color: SAND_D });
  for (let k = 0; k < 3; k++) b.add(at(new THREE.TorusGeometry(0.48 - k * 0.08, 0.04, 4, 10, Math.PI), 0, 0.4, -0.25 - k * 0.22, 0, 0, 0, [1, 0.45, 1]), { color: SAND_D });
  for (const [p, t, r] of [[[0.18, 0.5, -0.1], [0.36, 1.05, -0.25], 0.09], [[-0.2, 0.5, -0.3], [-0.38, 0.98, -0.5], 0.085], [[0.05, 0.52, -0.45], [0.1, 1.12, -0.7], 0.1], [[-0.1, 0.5, 0.05], [-0.2, 0.86, -0.02], 0.07], [[0.3, 0.46, -0.42], [0.5, 0.8, -0.6], 0.06]] as [V3, V3, number][]) {
    b.add(shard(p, t, r, 5), { color: CRYS, glow: r >= 0.085 });
  }
  for (const s of [-1, 1]) {
    b.add(blob(0.04, s * 0.12, 0.5, 0.62), { color: EYE, glow: true });
    const jaw = { pivot: [s * 0.14, 0.4, 0.6] as V3, raise: 0, flap: -s * 0.7 };
    const pts: V3[] = [[s * 0.14, 0.4, 0.6], [s * 0.34, 0.38, 0.92], [s * 0.2, 0.36, 1.18]];
    b.add(limb(pts[0], pts[1], 0.06, 0.05, 5), { color: SAND_D, ...jaw });
    b.add(limb(pts[1], pts[2], 0.05, 0.015, 5), { color: PAL.ivory, ...jaw });
    for (const z of [0.25, -0.05, -0.35]) { const hp: V3 = [s * 0.42, 0.36, z]; b.add(limb(hp, [s * 0.82, 0.02, z + 0.08], 0.05, 0.025, 4), { color: SAND_D, pivot: hp, swing: 0.5, sphase: legP(z === -0.05 ? -s : s) }); }
  }
  return b.build();
}

const art = (name: string, build: () => ArtGeo, stride: number, bob: number, hover = 0, outline = 0.03): EnemyArt => ({ name, build: () => build(), stride, bob, hover, outline });

/** map-only foe looks (merged into FAMILY_ART in index.ts; renderer.ts builds only the current map's) */
export const MAP_FOE_ART: Record<MapFoeKind, EnemyArt> = {
  delver: art('Loam Delver', delver, 2.2, 0.05),
  jammer: art('Hush Beacon', jammer, 1.2, 0.02, 1.5, 0.025),
  roller: art('Rust Roller', roller, 1.4, 0.06, 0, 0.04),
  sporepod: art('Bloat Pod', sporepod, 0.6, 0, 0, 0.03),
  lasher: art('Lash Creeper', lasher, 1.6, 0.05),
  wraith: art('Rime Wraith', wraith, 1.6, 0.02, 1.1, 0.025),
  hopper: art('Slag Hopper', hopper, 1.8, 0.05),
  mite: art('Cinder Mite', mite, 3.4, 0.03, 0, 0.018),
  kite: art('Gust Kite', kite, 1.2, 0.02, 1.8, 0.022),
  skyray: art('Sky Ray', skyray, 0.9, 0.02, 2.4, 0.025),
  lurker: art('Dune Lurker', lurker, 2.0, 0.04, 0, 0.03),
};
